import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface FixedConfig {
  id: string;
  category_id: string;
  amount_cents: number;
  due_day: number;
  autopay: boolean;
  is_paused: boolean;
  alternate_turn: boolean;
  paid_by?: string;
}

interface CategoryDefault {
  id: string;
  category_id: string;
  action: 'split' | 'keep';
  split_ratio: number;
}

interface Category {
  id: string;
  space_id: string;
  name: string;
  emoji: string;
  is_fixed: boolean;
  is_custom: boolean;
  is_pinned: boolean;
  is_hidden: boolean;
  sort_order: number;
  fixed_configs?: FixedConfig[];
  category_defaults?: CategoryDefault[];
}

interface CategoryState {
  categories: Category[];
  isLoading: boolean;
  fetchCategories: (spaceId: string) => Promise<void>;
  createCategory: (category: Partial<Category>) => Promise<any>;
  createCategoryWithConfig: (category: Partial<Category>, fixedConfig?: Partial<FixedConfig>, defaultPref?: { action: 'split' | 'keep'; split_ratio: number }) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  updateCategoryDefault: (categoryId: string, spaceId: string, action: 'split' | 'keep', split_ratio: number) => Promise<void>;
  getCategoryDefault: (categoryId: string) => { action: 'split' | 'keep'; split_ratio: number } | null;
}

export const useCategoryStore = create<CategoryState>((set, get) => ({
  categories: [],
  isLoading: false,

  fetchCategories: async (spaceId) => {
    set({ isLoading: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`/api/categories/${spaceId}`, {
        headers: {
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
      });

      const payload = await response.json().catch(() => []);
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to fetch categories');
      }

      set({ categories: Array.isArray(payload) ? payload : [] });
    } finally {
      set({ isLoading: false });
    }
  },

  createCategory: async (category) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/categories', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(category),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to create category');
    }

    set({ categories: [...get().categories, payload] });
    return payload;
  },

  createCategoryWithConfig: async (category, fixedConfig, defaultPref) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/categories', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({
        ...category,
        fixedConfig,
        defaultPref,
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to create category');
    }

    set({ categories: [...get().categories, payload] });
  },

  updateCategory: async (id, updates) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/categories/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify(updates),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to update category');
    }

    set({
      categories: get().categories.map(c => c.id === id ? { ...c, ...payload } : c)
    });
  },

  deleteCategory: async (id) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/categories/${id}`, {
      method: 'DELETE',
      headers: {
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to delete category');
    }

    set({ categories: get().categories.filter(c => c.id !== id) });
  },

  updateCategoryDefault: async (categoryId, spaceId, action, split_ratio) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/categories/${categoryId}/defaults`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({
        space_id: spaceId,
        action,
        split_ratio,
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to update category default');
    }

    set({
      categories: get().categories.map((category) =>
        category.id === categoryId
          ? { ...category, category_defaults: [payload] }
          : category
      ),
    });
  },

  getCategoryDefault: (categoryId) => {
    const category = get().categories.find(c => c.id === categoryId);
    if (category?.category_defaults?.[0]) {
      return {
        action: category.category_defaults[0].action,
        split_ratio: category.category_defaults[0].split_ratio
      };
    }
    return { action: 'split' as const, split_ratio: 50 };
  }
}));
